// BalanceValidation.tsx
import React, { useState, Dispatch, SetStateAction } from 'react';
import { Form } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { InvoiceData, GlobalCheck } from '../../../../types';
import './BalanceValidation.scss';
import { formatCurrency } from '../../../../../../../../../../utils/functions';

interface Props {
  invoiceData:     InvoiceData;
  globalValues:    Record<string, string>;
  setGlobalValues: Dispatch<SetStateAction<Record<string, string>>>;
  itemValues:      Record<string, string>;
  setItemValues:   Dispatch<SetStateAction<Record<string, string>>>;
  // NEW: Only show balances for these item indices (original indices from detalleItems)
  includedItemIndices?: number[];
}

type TupleKeys =
  | 'totalNeto' | 'totalBruto' | 'totalIva' | 'totalExtra'
  | 'totalTransporte' | 'totalDescuento' | 'fechaEmision' | 'fechaPago';

export const globalBalanceKeys: TupleKeys[] = [
  'totalNeto', 'totalBruto', 'totalIva', 'totalExtra', 'totalTransporte',
  'totalDescuento', 'fechaEmision', 'fechaPago'
];
export const editableItemFields = [
  'itemBrutoTotal', 'itemNetoTotal', 'itemIva', 'itemExtra', 'itemTransporte',
] as const;

const GLOBAL_LABELS: Record<TupleKeys, string> = {
  totalNeto: 'Total Neto',
  totalBruto: 'Total Bruto',
  totalIva: 'Total IVA',
  totalExtra: 'Total Extra',
  totalTransporte: 'Total Transporte',
  totalDescuento: 'Total Descuento',
  fechaEmision: 'Fecha Emisión',
  fechaPago: 'Fecha Pago',
};

const GLOBAL_CHECK_MAP: Record<string, TupleKeys> = {
  CHK001: 'totalNeto', CHK002: 'totalBruto', CHK003: 'totalIva',
  CHK004: 'totalExtra', CHK005: 'totalTransporte', CHK006: 'totalDescuento',
};

export default function BalanceValidation({
  invoiceData: data,
  globalValues,
  setGlobalValues,
  itemValues,
  setItemValues,
  includedItemIndices,
}: Props) {
  // Local state for UI only (editing flags, expand flags) is preserved
  const [editingGlobal, setEditingGlobal] = useState<Partial<Record<TupleKeys, boolean>>>({});
  const [expandGlobal, setExpandGlobal] = useState<Partial<Record<TupleKeys, boolean>>>({});
  const [editingItem, setEditingItem] = useState<Record<string, boolean>>({});
  const [expandItem, setExpandItem] = useState<Record<number, boolean>>({});

  const failedGlobals = data.detalleChecks
    .filter((chk: GlobalCheck) => chk.resultado === '0')
    .map((chk) => chk.checkId);

  const globalRows = globalBalanceKeys.map((key) => {
    const hasFail = failedGlobals.some((id) => GLOBAL_CHECK_MAP[id] === key);
    const rowChecks = data.detalleChecks.filter(
      (c) => GLOBAL_CHECK_MAP[c.checkId] === key && c.resultado === '0'
    );
    return { key, label: GLOBAL_LABELS[key], value: globalValues[key] || '', warning: hasFail, checks: rowChecks };
  });

  // Determine which item indices to render. Default: all.
  const indices: number[] = includedItemIndices && includedItemIndices.length > 0
    ? includedItemIndices
    : data.detalleItems.map((_, idx) => idx);

  return (
    <div className="balance-validation-step">
      <h5>3. Validar Balances</h5>
      <h6>Información general</h6>
      <div className="responsive-table-wrapper">
        <table className="balance-table">
          <thead>
            <tr>
              <th>Campo</th>
              <th>Valor</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {globalRows.map(({ key, label, value, warning, checks }) => (
              <React.Fragment key={key}>
                <tr className={warning ? 'warn' : 'ok'}>
                  <td data-label="Campo">{label}</td>
                  <td data-label="Valor" className="editable-cell" onClick={() => setEditingGlobal((e) => ({ ...e, [key]: true }))}>
                    {editingGlobal[key] ? (
                      <Form.Control type="text" value={value}
                        onChange={(e) => setGlobalValues((gv) => ({ ...gv, [key]: e.target.value }))}
                        onBlur={() => setEditingGlobal((e) => ({ ...e, [key]: false }))}
                        autoFocus
                      />
                    ) : ( key.toLowerCase().includes('total') ? formatCurrency(Number(value)) : value )}
                  </td>
                  <td data-label="Estado" className="status-cell" onClick={() => warning && setExpandGlobal((ex) => ({ ...ex, [key]: !ex[key] }))}>
                    {warning ? <FaExclamationTriangle className="icon-warn" /> : <FaCheckCircle className="icon-ok" />}
                  </td>
                </tr>
                {warning && expandGlobal[key] && (
                  <tr className="warning-details-row">
                    <td colSpan={3}>
                      <ul className="warning-list">
                        {checks.map((c) => ( <li key={c.checkId}><strong>{c.checkId}:</strong> {c.mensajeError}</li> ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <h6>Información por Producto</h6>
      <div className="responsive-table-wrapper">
        <table className="balance-table items-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Bruto Total</th>
              <th>Neto Total</th>
              <th>IVA</th>
              <th>Extra</th>
              <th>Transporte</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {indices.map((idx) => {
              const item = data.detalleItems[idx];
              const checksFail = item.checksItem.some((c: any) => c.resultado === '0');
              return (
                <React.Fragment key={idx}>
                  <tr className={checksFail ? 'warn' : 'ok'}>
                    <td data-label="#">{idx + 1}</td>
                    <td data-label="Nombre">{item.nombre[0]}</td>
                    {(editableItemFields as readonly string[]).map((field) => {
                      const cellKey = `${idx}-${field}`;
                      const rawVal = itemValues[cellKey] ?? '';
                      return (
                        <td data-label={field.replace('item','').replace('Total','')} className="editable-cell" key={field} onClick={() => setEditingItem((e) => ({ ...e, [cellKey]: true }))}>
                          {editingItem[cellKey] ? (
                            <Form.Control type="text" value={rawVal}
                              onChange={(e) => setItemValues((iv) => ({ ...iv, [cellKey]: e.target.value }))}
                              onBlur={() => setEditingItem((e) => ({ ...e, [cellKey]: false }))}
                              autoFocus
                            />
                          ) : ( formatCurrency(Number(rawVal)) )}
                        </td>
                      );
                    })}
                    <td data-label="Estado" className="status-cell" onClick={() => checksFail && setExpandItem((ex) => ({ ...ex, [idx]: !ex[idx] }))}>
                        {checksFail ? <FaExclamationTriangle className="icon-warn" /> : <FaCheckCircle className="icon-ok" />}
                    </td>
                  </tr>
                  {checksFail && expandItem[idx] && (
                    <tr className="warning-details-row">
                      <td colSpan={8}>
                        <ul className="warning-list">
                            {item.checksItem.filter((c: any) => c.resultado === '0').map((c: any) => ( <li key={c.checkId}><strong>{c.checkId}:</strong> {c.mensajeError}</li> ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
